/**
 * @fileoverview A program viewer's Lessons tab: every session of the program,
 * held and to come, in a table sorted by any column and exported as CSV.
 *
 * Read from `GET /org/viewer/programs/{id}/lessons`, which writes each row
 * for the viewer's grants: attendance with the attendance grant, the title
 * and summary an instructor shared with families with the lesson-notes grant.
 * A summary is never sent for a lesson that taught a student under 13 (or one
 * whose age group is not set); the server counts those and the tab says so.
 * Whether an instructor has written their notes yet is the organization's
 * business, not the viewer's, so a row says only that the session was held.
 *
 * Held, Upcoming or All lives in the URL filter, like every dashboard tab.
 *
 * Oldest first, as every lesson list on the instructor and organization sides
 * reads (lib/lessonHistory `sortChronologically`). Held opens on its first 25,
 * the program's earliest sessions, with the later ones a click below
 * (MoreRows), like the org's Lessons tab. Upcoming runs the rest of the term
 * from the next session, and All the whole term, unlimited.
 */

import { useEffect, useMemo, useState } from "react";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, Icon, Tag, TextTabs } from "@/components/studio/primitives";
import { CSV_TYPE, ExportButton } from "@/components/studio/ExportMenu";
import ReportTable from "@/components/org/ReportTable";
import { MoreRowsBar, usePagedRows } from "@/components/studio/MoreRows";
import { sortChronologically } from "@/lib/lessonHistory";
import { formatTime12h, shortDateLabel } from "@/lib/schedule";
import { attendedLabel, attendedRatio, plural } from "@/lib/viewerPortal";

const TEXT = "text-left font-sans text-[var(--text-primary)]";
const note = "px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]";
const select =
  "rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

const STATUS_TAGS = { upcoming: { label: "Upcoming" }, cancelled: { label: "No class" } };

/** Who taught it: the substitute when there was one. */
const taughtBy = (r) => r.substitute_name || r.instructor_name || "";

function columns({ attendance, notes, showStatus, openId }) {
  const cols = [
    {
      id: "date",
      label: "Date",
      className: TEXT,
      sortValue: (r) => `${r.date} ${r.time_local || ""}`,
      render: (r) => (
        <div className="whitespace-nowrap">
          <div>{shortDateLabel(r.date)}</div>
          <div className="font-sans text-[12px] text-[var(--text-tertiary)]">{formatTime12h(r.time_local)}</div>
        </div>
      ),
    },
    { id: "class", label: "Class", firstDir: "asc", className: TEXT, sortValue: (r) => r.class_name || "", render: (r) => r.class_name },
    {
      id: "instructor",
      label: "Instructor",
      firstDir: "asc",
      className: TEXT,
      sortValue: taughtBy,
      render: (r) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {taughtBy(r)}
          {r.substitute_name && <Tag accent>Substitute</Tag>}
        </span>
      ),
    },
  ];
  if (showStatus) {
    cols.push({
      id: "status",
      label: "Status",
      firstDir: "asc",
      className: TEXT,
      sortValue: (r) => r.status,
      render: (r) => (STATUS_TAGS[r.status] ? <Tag>{STATUS_TAGS[r.status].label}</Tag> : <span className="text-[var(--text-tertiary)]">Held</span>),
    });
  }
  if (attendance) {
    cols.push({ id: "attended", label: "Attended", className: "font-sans tabular-nums", sortValue: (r) => attendedRatio(r.attendance), render: (r) => attendedLabel(r.attendance) });
  }
  if (notes) {
    cols.push({
      id: "lesson",
      label: "Lesson notes",
      firstDir: "asc",
      className: `${TEXT} min-w-[14rem]`,
      sortValue: (r) => r.note?.title || "",
      render: (r) =>
        r.note ? (
          <span className="inline-flex items-center gap-1.5 text-[var(--text-primary)]">
            <span className="line-clamp-1">{r.note.title}</span>
            <Icon name={openId === r.lesson_id ? "chevronUp" : "chevronDown"} size={15} className="shrink-0 text-[var(--text-tertiary)]" />
          </span>
        ) : (
          <span className="text-[var(--text-tertiary)]">—</span>
        ),
    });
  }
  return cols;
}

/**
 * @param {{ programId: string, permissions: string[], filter: string|null,
 *   onFilter: (f: string|null) => void, title: string }} props
 */
export default function ViewerLessons({ programId, permissions, filter, onFilter, title }) {
  const [payload, setPayload] = useState(null);
  const [classId, setClassId] = useState("all");
  const [instructor, setInstructor] = useState("all");
  const [openId, setOpenId] = useState(null);
  const attendance = permissions.includes("attendance");
  const notes = permissions.includes("lesson_notes");
  const status = ["upcoming", "all"].includes(filter) ? filter : "held";

  useEffect(() => {
    let live = true;
    orgApi
      .get(`/org/viewer/programs/${programId}/lessons`)
      .then((res) => live && setPayload(res.data))
      .catch(() => live && setPayload({ sessions: [], failed: true }));
    return () => {
      live = false;
    };
  }, [programId]);

  const all = useMemo(() => payload?.sessions || [], [payload]);
  const counts = useMemo(
    () => ({
      held: all.filter((r) => r.status === "held").length,
      upcoming: all.filter((r) => r.status === "upcoming").length,
      all: all.length,
    }),
    [all],
  );
  const classes = useMemo(() => [...new Map(all.map((r) => [r.class_id, r.class_name])).entries()], [all]);
  const instructors = useMemo(() => [...new Set(all.map(taughtBy).filter(Boolean))].sort(), [all]);
  const filtered = useMemo(
    () =>
      sortChronologically(
        all.filter(
          (r) =>
            (status === "all" || r.status === status) &&
            (classId === "all" || r.class_id === classId) &&
            (instructor === "all" || taughtBy(r) === instructor),
        ),
      ),
    [all, status, classId, instructor],
  );
  const paged = usePagedRows(filtered, [status, classId, instructor].join("|"));
  const shown = status === "held" ? paged.shown : filtered;
  const cols = useMemo(() => columns({ attendance, notes, showStatus: status !== "held", openId }), [attendance, notes, status, openId]);

  const exportItem = {
    url: `/org/viewer/programs/${programId}/export/lessons`,
    filename: `hans-lessons-${(title || "program").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`,
    type: CSV_TYPE,
    label: "Export lessons",
    what: "lessons",
  };

  return (
    <div data-testid="viewer-lessons">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <TextTabs
            testId="viewer-lessons-filter"
            value={status}
            onChange={(id) => onFilter(id === "held" ? null : id)}
            options={[
              { id: "held", label: "Held", count: counts.held },
              { id: "upcoming", label: "Upcoming", count: counts.upcoming },
              { id: "all", label: "All", count: counts.all },
            ]}
          />
        </div>
        <ExportButton client={orgApi} errorMessage={getOrgErrorMessage} item={exportItem} testId="viewer-lessons-export" />
      </div>

      {(classes.length > 1 || instructors.length > 1) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {classes.length > 1 && (
            <select value={classId} onChange={(e) => setClassId(e.target.value)} aria-label="Filter by class" data-testid="viewer-lessons-class" className={select}>
              <option value="all">All classes</option>
              {classes.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          )}
          {instructors.length > 1 && (
            <select value={instructor} onChange={(e) => setInstructor(e.target.value)} aria-label="Filter by instructor" data-testid="viewer-lessons-instructor" className={select}>
              <option value="all">All instructors</option>
              {instructors.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <Card padding={0} className="mb-3">
        {!payload ? (
          <div className={note}>Loading…</div>
        ) : payload.failed ? (
          <div className={note}>Couldn&rsquo;t load the lessons.</div>
        ) : (
          <ReportTable
            key={status}
            testId="viewer-lessons-table"
            columns={cols}
            rows={shown}
            rowKey={(r) => r.lesson_id}
            defaultSort={{ id: "date", dir: "asc" }}
            onRowPress={(r) => setOpenId((id) => (id === r.lesson_id ? null : r.lesson_id))}
            canPress={(r) => Boolean(r.note)}
            renderAfter={(r) =>
              r.lesson_id === openId && r.note ? (
                <p className="max-w-3xl whitespace-pre-line rounded-[12px] bg-[var(--studio-overlay)] px-4 py-3 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]" data-testid={`viewer-lesson-summary-${r.lesson_id}`}>
                  {r.note.summary}
                </p>
              ) : null
            }
            empty={all.length === 0 ? "No sessions yet." : "Nothing matches these filters."}
          />
        )}
      </Card>

      {payload && !payload.failed && status === "held" && (
        <MoreRowsBar paged={paged} unit="session" testId="viewer-lessons-more" className="mb-3" />
      )}

      {payload && !payload.failed && (
        <p className="max-w-3xl font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
          {plural(counts.held, "session")} held so far. {notes ? "Open a lesson to read the summary its instructor shared with families. " : ""}
          {payload.withheld > 0 &&
            `Notes from ${plural(payload.withheld, "session")} aren’t shown because they include a student under 13, or one whose age group isn’t set; those go to parents only. `}
          Exports are this program&rsquo;s rows as CSV, with only what has been shared with you.
        </p>
      )}
    </div>
  );
}
